import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { label: "Home", href: "#hero" },
  { label: "About", href: "/about", isRoute: true },
  { label: "Shop", href: "/shop", isRoute: true },
  { label: "Contact", href: "/contact", isRoute: true },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const navBg = !isHome || scrolled
    ? "backdrop-blur-xl bg-white/95 border-b border-black/5 shadow-sm"
    : "bg-transparent";

  const textColor = !isHome || scrolled ? "text-[#6B7280]" : "text-white/80";
  const textHover = !isHome || scrolled ? "hover:text-[#1A1A1A]" : "hover:text-white";

  return (
    <motion.nav
      data-testid="navbar"
      aria-label="Main navigation"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`}
    >
      <div className="container mx-auto px-6 sm:px-8 max-w-7xl flex items-center justify-between h-20">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3">
          <img src="/eagle_logo_cropped.png" alt="Emcee Sons Diary House" className="h-14 sm:h-16 w-auto" />
        </Link>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            link.isRoute ? (
              <Link
                key={link.label}
                to={link.href}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`text-sm ${textColor} ${textHover} transition-colors duration-300 tracking-wide`}
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
              >
                {link.label}
              </Link>
            ) : (
              isHome ? (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                  className={`text-sm ${textColor} ${textHover} transition-colors duration-300 tracking-wide`}
                  style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to="/"
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                  className={`text-sm ${textColor} ${textHover} transition-colors duration-300 tracking-wide`}
                  style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                >
                  {link.label}
                </Link>
              )
            )
          ))}
          <a
            href="tel:+919343716059"
            data-testid="nav-phone-btn"
            className="flex items-center gap-2 bg-[#7A1A31] text-white px-5 py-2.5 rounded-full hover:bg-[#5C162E] transition-colors duration-300 text-sm font-medium"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            <Phone className="w-4 h-4" />
            Call Now
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden p-2 ${!isHome || scrolled ? "text-[#1A1A1A]" : "text-white"}`}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden backdrop-blur-xl bg-white/98 border-b border-black/5"
          >
            <div className="container mx-auto px-6 py-6 flex flex-col gap-5">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    className="text-base text-[#6B7280] hover:text-[#1A1A1A] transition-colors"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <Link
                    key={link.label}
                    to="/"
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    className="text-base text-[#6B7280] hover:text-[#1A1A1A] transition-colors"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {link.label}
                  </Link>
                )
              ))}
              <a
                href="tel:+919343716059"
                data-testid="mobile-phone-btn"
                className="flex items-center gap-2 bg-[#7A1A31] text-white px-5 py-3 rounded-full w-fit text-sm font-medium"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
